/**
 * legislativeActions.js
 * 
 * Unified categorization system for Georgia General Assembly legislative actions.
 * Maps all ~45 action codes to:
 *   1. Timeline buckets (for BillTimeline visual display)
 *   2. Status labels (for filtering and database storage)
 *   3. Action types (for coloring and UI feedback)
 * 
 * Based on analysis of 3,502 bills in the Supabase database.
 */

// ============================================================================
// ACTION CODE DEFINITIONS
// ============================================================================

/**
 * Complete dictionary of GGA action codes with metadata.
 * Each code includes:
 *   - description: Human-readable description
 *   - chamber: 'house', 'senate', 'both', or 'executive'
 *   - bucket: Which timeline bucket this belongs to
 *   - actionType: Visual classification ('positive', 'negative', 'neutral', 'procedural')
 *   - isTerminal: Whether this ends a bill's lifecycle
 *   - stage: Numeric stage in the process (1-7)
 */
export const ACTION_CODES = {
    // ═══════════════════════════════════════════════════════════════════════════
    // HOUSE INTRODUCTION (Stage 1)
    // ═══════════════════════════════════════════════════════════════════════════
    HH: {
        description: 'House Hopper',
        chamber: 'house',
        bucket: 'intro',
        actionType: 'neutral',
        isTerminal: false,
        stage: 1,
        proceduralOrder: 1
    },
    HFR: {
        description: 'House First Readers',
        chamber: 'house',
        bucket: 'intro',
        actionType: 'positive',
        isTerminal: false,
        stage: 1,
        proceduralOrder: 2
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SENATE INTRODUCTION (Stage 1)
    // ═══════════════════════════════════════════════════════════════════════════
    SH: {
        description: 'Senate Hopper',
        chamber: 'senate',
        bucket: 'intro',
        actionType: 'neutral',
        isTerminal: false,
        stage: 1,
        proceduralOrder: 1
    },
    SRAR: {
        description: 'Senate Read and Referred',
        chamber: 'senate',
        bucket: 'intro', // For origin; 'comm2' for crossover
        actionType: 'positive',
        isTerminal: false,
        stage: 1,
        proceduralOrder: 3
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // HOUSE COMMITTEE (Stage 2)
    // ═══════════════════════════════════════════════════════════════════════════
    HCFR: {
        description: 'House Committee Favorably Reported',
        alternateDescriptions: ['House Committee Favorably Reported By Substitute'],
        chamber: 'house',
        bucket: 'comm1', // Or 'comm2' if crossover
        actionType: 'positive',
        isTerminal: false,
        stage: 2,
        proceduralOrder: 4
    },
    HRECM: {
        description: 'House Withdrawn, Recommitted',
        chamber: 'house',
        bucket: 'comm1',
        actionType: 'procedural',
        isTerminal: false, // NOT terminal - bill can come back
        stage: 2,
        proceduralOrder: 7
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SENATE COMMITTEE (Stage 2)
    // ═══════════════════════════════════════════════════════════════════════════
    SCFR: {
        description: 'Senate Committee Favorably Reported',
        alternateDescriptions: ['Senate Committee Favorably Reported By Substitute'],
        chamber: 'senate',
        bucket: 'comm1', // Or 'comm2' if crossover
        actionType: 'positive',
        isTerminal: false,
        stage: 2,
        proceduralOrder: 4
    },
    'SW&C': {
        description: 'Senate Withdrawn & Recommitted',
        chamber: 'senate',
        bucket: 'comm1',
        actionType: 'procedural',
        isTerminal: false,
        stage: 2,
        proceduralOrder: 7
    },
    SR: {
        description: 'Senate Recommitted',
        chamber: 'senate',
        bucket: 'comm1', // Recommitted goes back to committee (Origin or Second)
        actionType: 'procedural',
        isTerminal: false,
        stage: 2,
        proceduralOrder: 7
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // HOUSE FLOOR (Stage 3)
    // ═══════════════════════════════════════════════════════════════════════════
    HSR: {
        description: 'House Second Readers',
        chamber: 'house',
        bucket: 'floor1', // Or 'floor2' if crossover
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 5
    },
    HTR: {
        description: 'House Third Readers',
        chamber: 'house',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    HPA: {
        description: 'House Passed/Adopted',
        alternateDescriptions: ['House Passed/Adopted By Substitute'],
        chamber: 'house',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 8
    },
    HRA: {
        description: 'House Read and Adopted',
        chamber: 'house',
        bucket: 'floor1', // For resolutions - ends here
        actionType: 'positive',
        isTerminal: true, // Terminal for HR only
        stage: 3,
        proceduralOrder: 8
    },
    HTRL: {
        description: 'House Third Reading Lost',
        chamber: 'house',
        bucket: 'floor1',
        actionType: 'negative',
        isTerminal: true,
        stage: 3,
        proceduralOrder: 8
    },
    HPOST: {
        description: 'House Postponed',
        chamber: 'house',
        bucket: 'floor1',
        actionType: 'procedural',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    HRECO: {
        description: 'House Reconsidered',
        chamber: 'house',
        bucket: 'floor1',
        actionType: 'procedural',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    HTS: {
        description: 'House Immediately Transmitted to Senate',
        chamber: 'house',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 9
    },
    HWREC: {
        description: 'House Withdrawn Bill/Res Consideration',
        chamber: 'house',
        bucket: 'floor1',
        actionType: 'negative',
        isTerminal: true,
        stage: 3,
        proceduralOrder: 10
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SENATE FLOOR (Stage 3)
    // ═══════════════════════════════════════════════════════════════════════════
    S2R: {
        description: 'Senate Read Second Time',
        chamber: 'senate',
        bucket: 'floor1', // Or 'floor2' if crossover
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 5
    },
    STR: {
        description: 'Senate Third Read',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    SPA: {
        description: 'Senate Passed/Adopted',
        alternateDescriptions: ['Senate Passed/Adopted By Substitute', 'Senate Passed/Adopted As Amended'],
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 8
    },
    SRA: {
        description: 'Senate Read and Adopted',
        chamber: 'senate',
        bucket: 'floor1', // For resolutions - ends here
        actionType: 'positive',
        isTerminal: true, // Terminal for SR only
        stage: 3,
        proceduralOrder: 8
    },
    S3RLT: {
        description: 'Senate Lost',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'negative',
        isTerminal: true,
        stage: 3,
        proceduralOrder: 8
    },
    STAB: {
        description: 'Senate Tabled',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'procedural',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    STT: {
        description: 'Senate Taken from Table',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    SRECO: {
        description: 'Senate Reconsidered',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'procedural',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    SRECP: {
        description: 'Senate Reconsidered Passed/Adopted As Amended',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 8
    },
    SNOM: {
        description: 'Senate Notice to Reconsider',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'procedural',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 6
    },
    STH: {
        description: 'Senate Transmitted House',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 3,
        proceduralOrder: 9
    },
    SENG: {
        description: 'Senate Engrossed',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'positive',
        isTerminal: false,
        stage: 4,
        proceduralOrder: 9
    },
    SWREC: {
        description: 'Senate Withdrawn Bill/Res Consideration',
        chamber: 'senate',
        bucket: 'floor1',
        actionType: 'negative',
        isTerminal: true,
        stage: 3,
        proceduralOrder: 10
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // RECONCILIATION (Stage 5)
    // ═══════════════════════════════════════════════════════════════════════════
    HASAS: {
        description: 'House Agreed Senate Amend or Sub',
        alternateDescriptions: ['House Agreed Senate Amend or Sub As Amended'],
        chamber: 'house',
        bucket: 'recon',
        actionType: 'positive',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    HDSAS: {
        description: 'House Disagreed Senate Amend or Sub',
        chamber: 'house',
        bucket: 'recon',
        actionType: 'procedural',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    HI: {
        description: 'House Insisted',
        chamber: 'house',
        bucket: 'recon',
        actionType: 'procedural',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    HCAP: {
        description: 'House Conference Committee Appointed',
        chamber: 'house',
        bucket: 'recon',
        actionType: 'procedural',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    HCA: {
        description: 'House Conference Committee Report Adopted',
        chamber: 'house',
        bucket: 'recon',
        actionType: 'positive',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    SAHAS: {
        description: 'Senate Agreed House Amend or Sub',
        chamber: 'senate',
        bucket: 'recon',
        actionType: 'positive',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    SREC: {
        description: 'Senate Receded from Amend or Sub',
        chamber: 'senate',
        bucket: 'recon',
        actionType: 'positive',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    SCAP: {
        description: 'Senate Conference Committee Appointed',
        chamber: 'senate',
        bucket: 'recon',
        actionType: 'procedural',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },
    SCA: {
        description: 'Senate Conference Committee Report Adopted',
        chamber: 'senate',
        bucket: 'recon',
        actionType: 'positive',
        isTerminal: false,
        stage: 5,
        proceduralOrder: 10
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // GOVERNOR (Stage 6)
    // ═══════════════════════════════════════════════════════════════════════════
    HSG: {
        description: 'House Sent to Governor',
        chamber: 'house',
        bucket: 'gov',
        actionType: 'positive',
        isTerminal: false,
        stage: 6,
        proceduralOrder: 11
    },
    HDSG: {
        description: 'House Date Signed by Governor',
        chamber: 'executive',
        bucket: 'gov',
        actionType: 'positive',
        isTerminal: true,
        stage: 6,
        proceduralOrder: 11
    },
    SSG: {
        description: 'Senate Sent to Governor',
        chamber: 'senate',
        bucket: 'gov',
        actionType: 'positive',
        isTerminal: false,
        stage: 6,
        proceduralOrder: 11
    },
    SDSG: {
        description: 'Senate Date Signed by Governor',
        chamber: 'executive',
        bucket: 'gov',
        actionType: 'positive',
        isTerminal: true,
        stage: 6,
        proceduralOrder: 11
    },
    'Signed Gov': {
        description: 'Signed by Governor / Act Number or Veto',
        chamber: 'executive',
        bucket: 'gov',
        actionType: 'positive', // Could be negative if Veto
        isTerminal: true,
        stage: 6,
        proceduralOrder: 11
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // FINAL / EFFECTIVE (Stage 7)
    // ═══════════════════════════════════════════════════════════════════════════
    EFF: {
        description: 'Effective Date',
        chamber: 'executive',
        bucket: 'gov',
        actionType: 'positive',
        isTerminal: true,
        stage: 7,
        proceduralOrder: 12
    }
};

// ============================================================================
// STATUS LABELS (for database filtering)
// ============================================================================

/**
 * All possible status labels used for filtering.
 * Maps to computed status_label in sync-gga-bills.js and bills-api.js
 */
export const STATUS_LABELS = {
    // Active/In-Progress
    INTRODUCED: 'Introduced',
    IN_COMMITTEE: 'In Committee',
    ON_THE_FLOOR: 'On the Floor',

    // Passage milestones
    PASSED_ONE_CHAMBER: 'Passed One Chamber',
    PASSED_BOTH_CHAMBERS: 'Passed Both Chambers',
    GOVERNORS_DESK: "Governor's Desk",

    // Final outcomes - Positive
    SIGNED_INTO_LAW: 'Signed into Law',
    ADOPTED: 'Adopted', // Resolutions only

    // Final outcomes - Negative
    VETOED: 'Vetoed',
    WITHDRAWN: 'Dead', // Consolidated
    DEAD_FAILED: 'Dead', // Consolidated

    // Procedural
    INACTIVE_STALLED: 'Stalled', // Unused mostly
    RESOLVING_DIFFERENCES: 'Resolving Differences'
};

/**
 * Status labels grouped by category for UI display.
 */
export const STATUS_GROUPS = {
    active: [
        STATUS_LABELS.INTRODUCED,
        STATUS_LABELS.IN_COMMITTEE,
        STATUS_LABELS.ON_THE_FLOOR
    ],
    progressing: [
        STATUS_LABELS.PASSED_ONE_CHAMBER,
        STATUS_LABELS.PASSED_BOTH_CHAMBERS,
        STATUS_LABELS.GOVERNORS_DESK,
        STATUS_LABELS.RESOLVING_DIFFERENCES
    ],
    completed: [
        STATUS_LABELS.SIGNED_INTO_LAW,
        STATUS_LABELS.ADOPTED
    ],
    failed: [
        STATUS_LABELS.VETOED,
        // STATUS_LABELS.WITHDRAWN, // Merged into Dead
        STATUS_LABELS.DEAD_FAILED,
        // STATUS_LABELS.INACTIVE_STALLED // Removed
    ]
};

// ============================================================================
// TIMELINE BUCKETS (for visual display in BillTimeline.jsx)
// ============================================================================

/**
 * Timeline bucket definitions for BillTimeline component.
 */
export const TIMELINE_BUCKETS = {
    intro: {
        id: 'intro',
        title: (origin) => `Introduced in ${origin}`,
        tooltip: 'Formal introduction and first reading.',
        keywords: ['introduced', 'read 1st', 'hopper', 'prefile', 'first readers'],
        completion: ['read'],
        stage: 1
    },
    comm1: {
        id: 'comm1',
        title: (origin) => `${origin} Committee`,
        tooltip: "Review by the originating chamber's committee.",
        keywords: ['committee', 'referred', 'favorable', 'report'],
        completion: ['favorably reported', 'release'],
        stage: 2
    },
    floor1: {
        id: 'floor1',
        title: (origin) => `${origin} Floor Vote`,
        tooltip: (origin) => `Vote by the full ${origin}.`,
        keywords: ['second readers', 'third readers', 'passed', 'adopted'],
        completion: ['passed', 'adopted'],
        failure: ['lost', 'failed', 'withdrawn'],
        stage: 3
    },
    comm2: {
        id: 'comm2',
        title: (second) => `${second} Committee`,
        tooltip: (second) => `Review by the ${second}'s committee.`,
        keywords: ['committee', 'referred', 'favorable', 'report'],
        completion: ['favorably reported', 'release'],
        stage: 4
    },
    floor2: {
        id: 'floor2',
        title: (second) => `${second} Floor Vote`,
        tooltip: (second) => `Vote by the full ${second}.`,
        keywords: ['second readers', 'third readers', 'passed', 'adopted'],
        completion: ['passed', 'adopted'],
        failure: ['lost', 'failed'],
        stage: 5
    },
    recon: {
        id: 'recon',
        title: () => 'Reconciliation',
        tooltip: 'Resolving differences between House and Senate versions.',
        keywords: ['conference', 'agreed', 'insisted', 'receded', 'disagreed', 'amend or sub'],
        completion: ['agreed', 'adopted', 'receded'],
        isConditional: true, // Only shown if there are reconciliation actions
        stage: 6
    },
    gov: {
        id: 'gov',
        title: () => "Governor's Action",
        tooltip: 'Governor signs, vetoes, or allows bill to become law.',
        keywords: ['governor', 'signed', 'act ', 'effective date', 'veto'],
        completion: ['signed', 'act ', 'effective date'],
        failure: ['veto'],
        stage: 7
    }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Gets the action code info for a given code string.
 * @param {string} code - Action code (e.g., 'HFR', 'SPA')
 * @returns {Object|null} - Action code definition or null
 */
export function getActionCodeInfo(code) {
    return ACTION_CODES[code] || null;
}

/**
 * Determines which bucket an action belongs to based on bill origin.
 * @param {Object} action - Action object with code and action description
 * @param {string} originChamber - 'House' or 'Senate'
 * @returns {string} - Bucket ID ('intro', 'comm1', 'floor1', etc.)
 */
export function getBucketForAction(action, originChamber) {
    const code = action.code;
    const actionText = (action.action || '').toLowerCase();
    const codeInfo = ACTION_CODES[code];

    if (!codeInfo) {
        // Fallback: infer from action text
        if (actionText.includes('governor') || actionText.includes('signed') || actionText.includes('act ')) {
            return 'gov';
        }
        if (actionText.includes('conference') || actionText.includes('agreed') || actionText.includes('amend or sub')) {
            return 'recon';
        }
        return 'intro'; // Default fallback
    }

    const secondChamber = originChamber === 'House' ? 'Senate' : 'House';

    // Determine if this is origin chamber or crossover
    const isOriginChamber = codeInfo.chamber === originChamber.toLowerCase();
    const isSecondChamber = codeInfo.chamber === secondChamber.toLowerCase();

    // For reconciliation and governor actions, bucket is fixed
    if (codeInfo.bucket === 'recon' || codeInfo.bucket === 'gov') {
        return codeInfo.bucket;
    }

    // For chamber-specific actions, adjust based on crossover
    if (codeInfo.bucket === 'intro') {
        // If it's origin intro, stay in intro. If it's second chamber intro (e.g. First Readers in second chamber),
        // it marks the start of the committee process there.
        return isOriginChamber ? 'intro' : 'comm2';
    }

    if (codeInfo.bucket === 'comm1') {
        return isOriginChamber ? 'comm1' : 'comm2';
    }

    if (codeInfo.bucket === 'floor1') {
        return isOriginChamber ? 'floor1' : 'floor2';
    }

    return codeInfo.bucket;
}

/**
 * Gets the procedural order for an action (for sorting same-date actions).
 * @param {string} actionText - Action description text
 * @returns {number} - Procedural order (lower = earlier)
 */
export function getProceduralOrder(actionText) {
    const a = (actionText || '').toLowerCase();

    // Check action codes first (most accurate)
    for (const [code, info] of Object.entries(ACTION_CODES)) {
        if (a.includes(info.description.toLowerCase())) {
            return info.proceduralOrder;
        }
    }

    // Fallback keyword matching
    if (a.includes('hopper') || a.includes('prefile')) return 1;
    if (a.includes('introduced') || a.includes('read 1st') || a.includes('first read')) return 2;
    if (a.includes('referred') || a.includes('read and referred')) return 3;
    if (a.includes('favorably reported')) return 4;
    if (a.includes('read 2nd') || a.includes('second read')) return 5;
    if (a.includes('read 3rd') || a.includes('third read')) return 6;
    if (a.includes('withdrawn') || a.includes('recommit') || a.includes('tabled')) return 7;
    if (a.includes('passed') || a.includes('adopted')) return 8;
    if (a.includes('sent to') || a.includes('transmit')) return 9;
    if (a.includes('agreed') || a.includes('conference')) return 10;
    if (a.includes('governor') || a.includes('signed')) return 11;
    if (a.includes('effective')) return 12;

    return 5; // Default middle position
}

/**
 * Determines the visual status type for an action.
 * @param {string} actionText - Action description text
 * @returns {'positive' | 'negative' | 'neutral' | 'procedural'} - Action type
 */
export function getActionType(actionText) {
    const a = actionText.toLowerCase();

    // Procedural (not success or failure, just movement)
    if (a.includes('withdrawn') && (a.includes('recommit') || a.includes('referred'))) return 'procedural';
    if (a.includes('recommitted') || a.includes('tabled') || a.includes('insisted') || a.includes('disagreed')) return 'procedural';
    if (a.includes('reconsider') || a.includes('postponed')) return 'procedural';

    // Negative outcomes
    if (a.includes('lost') || a.includes('veto') || a.includes('did not pass') || a.includes('failed') || a.includes('dead')) return 'negative';
    if (a.includes('withdrawn') && !a.includes('recommit')) return 'negative';

    // Positive outcomes
    if (a.includes('passed') || a.includes('adopted') || a.includes('signed') || a.includes('favorably reported') || a.includes('act ')) return 'positive';
    if (a.includes('agreed') || a.includes('effective date')) return 'positive';

    return 'neutral';
}

/**
 * Computes the overall status label for a bill based on its history.
 * This mirrors the logic in sync-gga-bills.js for consistency.
 * @param {Object} bill - Bill object with history array
 * @returns {string} - Status label (e.g., 'Signed into Law', 'In Committee')
 */
export function computeStatusLabel(bill) {
    // IMPORTANT: For most statuses, prefer the database's pre-computed status_label
    // However, for time-based statuses, we need to recalculate since time has passed since sync:
    // - Inactive/Stalled (obvious - always recalculate)
    // - On the Floor (can become stalled after 14 days)
    // - In Committee (can become stalled after 21 days)
    const timeBasedStatuses = [
        STATUS_LABELS.INACTIVE_STALLED,
        STATUS_LABELS.ON_THE_FLOOR,
        STATUS_LABELS.IN_COMMITTEE
    ];

    if (bill.status_label && !timeBasedStatuses.includes(bill.status_label)) {
        return bill.status_label;
    }

    // For time-based statuses or missing status_label, compute from history
    if (!bill.history || bill.history.length === 0) {
        return STATUS_LABELS.INTRODUCED;
    }

    const history = Array.isArray(bill.history) ? bill.history : [];
    const billNumber = (bill.bill_number || '').toUpperCase();
    const isHouseBill = billNumber.startsWith('HB') || billNumber.startsWith('HR');
    const isSenateBill = billNumber.startsWith('SB') || billNumber.startsWith('SR');
    const isResolution = billNumber.includes('R') && !billNumber.includes('JR');

    // Helper to check if any history entry matches keywords
    const findAction = (keywords, exclude = []) => history.some(h => {
        const a = (h.action || '').toLowerCase();
        return keywords.some(k => a.includes(k)) && !exclude.some(ex => a.includes(ex));
    });

    // === FINAL OUTCOMES (Strict Last Action) ===

    // Signed into law
    if (findAction(['signed', 'act ', 'effective date'], ['veto'])) {
        return STATUS_LABELS.SIGNED_INTO_LAW;
    }

    // Vetoed (without override)
    if (findAction(['veto']) && !findAction(['override'])) {
        return STATUS_LABELS.VETOED;
    }

    // Resolution adopted
    if (isResolution && findAction(['adopted', 'read and adopted'])) {
        return STATUS_LABELS.ADOPTED;
    }

    // STRICT CHECK: Dead/Failed/Withdrawn must be the LAST action to be valid
    const lastAction = history[0]?.action?.toLowerCase() || '';

    // Group Withdrawn/Failed/Dead as "Dead"
    // Checks for specific failure keywords in the last action
    if (['failed', 'did not pass', 'lost', 'dead', 'died', 'withdrawn'].some(k => lastAction.includes(k))) {
        // Ensure "Withdrawn" isn't just "Withdrawn, Recommitted" (which is active)
        if (lastAction.includes('withdrawn') && lastAction.includes('recommit')) {
            // Do nothing, fall through to active states
        } else {
            // Double check it wasn't signed later (safety)
            if (!findAction(['signed', 'act ', 'effective date'])) {
                return STATUS_LABELS.DEAD_FAILED; // Label will be updated to "Dead"
            }
        }
    }

    // === GOVERNOR ===

    if (findAction(['sent to governor', 'transmitted to governor', 'delivered to governor'])) {
        return STATUS_LABELS.GOVERNORS_DESK;
    }

    // === RECONCILIATION ===

    const isReconciling = findAction(['conference', 'agreed to', 'insisted', 'receded', 'disagreed']);
    if (isReconciling) {
        return STATUS_LABELS.RESOLVING_DIFFERENCES;
    }

    // === PROGRESSION ===

    const passedHouse = findAction(['house passed', 'house adopted']);
    const passedSenate = findAction(['senate passed', 'senate adopted']);
    const originPassed = (isHouseBill && passedHouse) || (isSenateBill && passedSenate);

    if (passedHouse && passedSenate) {
        return STATUS_LABELS.PASSED_BOTH_CHAMBERS;
    }

    if (originPassed) {
        return STATUS_LABELS.PASSED_ONE_CHAMBER;
    }

    // === ACTIVE STATES ===

    const onFloor = findAction(['third readers', 'third read', 'second readers', 'second read', 'read 2nd', 'read 3rd', 'calendar', 'rules', 'floor']);
    // Treat "recommitted" as In Committee
    const inCommittee = findAction(['committee', 'referred', 'favorably reported', 'recommitted', 'recommit']);

    // Removed Stalled Checks - User requested removal of time-based inactive logic

    // Active States
    if (onFloor) {
        return STATUS_LABELS.ON_THE_FLOOR;
    }

    if (inCommittee) {
        return STATUS_LABELS.IN_COMMITTEE;
    }

    return STATUS_LABELS.INTRODUCED;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
    ACTION_CODES,
    STATUS_LABELS,
    STATUS_GROUPS,
    TIMELINE_BUCKETS,
    getActionCodeInfo,
    getBucketForAction,
    getProceduralOrder,
    getActionType,
    computeStatusLabel
};
